import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Play, Tv, Sparkles } from 'lucide-react';
import { useAuthStore } from '../lib/auth';

export const Route = createFileRoute('/auth/signin')({
  component: SignInPage,
});

function FloatingParticle({ delay, duration, x, y, size }: { delay: number; duration: number; x: string; y: string; size: number }) {
  return (
    <motion.div
      className="signin-particle"
      initial={{ opacity: 0, scale: 0 }}
      animate={{
        opacity: [0, 0.7, 0],
        scale: [0, 1, 0.5],
        y: [0, -100, -200],
        x: [0, Math.random() > 0.5 ? 30 : -30, 0],
      }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
      style={{ left: x, top: y, width: size, height: size }}
    />
  );
}

function SignInPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const navigate = useNavigate();
  const { authenticated, loading, signIn, continueAsGuest } = useAuthStore();

  useEffect(() => {
    if (authenticated) {
      navigate({ to: '/', search: { playMovie: undefined, resumeFrom: undefined } });
    }
  }, [authenticated, navigate]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        >
          <Loader2 className="w-10 h-10 text-red-600" />
        </motion.div>
      </div>
    );
  }

  if (authenticated) {
    return null;
  }

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError('');
    try {
      signIn();
    } catch {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGuestMode = () => {
    continueAsGuest();
    navigate({ to: '/' });
  };

  const handleActivationCode = () => {
    navigate({ to: '/tv-activate' });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 120, damping: 14 },
    },
  };

  return (
    <div className="signin-page" onMouseMove={handleMouseMove}>
      {/* Layered background */}
      <div className="signin-bg-base" />
      <div className="signin-bg-mesh" />
      <div
        className="signin-spotlight"
        style={{
          background: `radial-gradient(700px circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(229, 9, 20, 0.08), transparent 55%)`,
        }}
      />

      {/* Floating particles — fewer, bigger, brighter */}
      {Array.from({ length: 12 }).map((_, i) => (
        <FloatingParticle
          key={i}
          delay={i * 1.1}
          duration={5 + Math.random() * 4}
          x={`${8 + Math.random() * 84}%`}
          y={`${30 + Math.random() * 60}%`}
          size={3 + Math.random() * 3}
        />
      ))}

      {/* Ambient orbs — stronger colours */}
      <motion.div
        className="signin-orb signin-orb-1"
        animate={{
          scale: [1, 1.35, 1],
          opacity: [0.25, 0.45, 0.25],
          x: [0, 40, 0],
          y: [0, -25, 0],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="signin-orb signin-orb-2"
        animate={{
          scale: [1.15, 0.85, 1.15],
          opacity: [0.15, 0.35, 0.15],
          x: [0, -50, 0],
          y: [0, 35, 0],
        }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
      />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10 w-full max-w-[420px] mx-auto"
      >
        {/* ── Logo ── */}
        <motion.div variants={itemVariants} className="text-center mb-7">
          <motion.div
            className="signin-logo-wrap"
            whileHover={{ scale: 1.04 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <div className="flex items-center justify-center gap-3">
              <motion.div
                className="signin-logo-img"
                animate={{ rotateY: [0, 8, -8, 0] }}
                transition={{ duration: 5, repeat: Infinity, repeatDelay: 4, ease: 'easeInOut' }}
              >
                <img
                  src="/logo.png"
                  alt="MoviesVault Logo"
                  className="w-full h-full object-contain"
                />
                <div className="signin-logo-glow" />
              </motion.div>

              <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-none">
                <span className="text-gradient-netflix-enhanced">Movies</span>
                <span className="text-white">Vault</span>
              </h1>
            </div>
          </motion.div>

          <motion.p
            variants={itemVariants}
            className="mt-3 text-[13px] text-gray-500 font-medium tracking-[0.25em] uppercase"
          >
            Stream Unlimited
          </motion.p>
        </motion.div>

        {/* ── Card ── */}
        <motion.div variants={itemVariants} className="signin-card">
          <motion.div
            className="signin-card-border"
            animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
          />
          <div className="signin-card-inner">
            {/* Badge + Heading */}
            <div className="text-center mb-7">
              <motion.div
                className="signin-badge"
                animate={{
                  boxShadow: [
                    '0 0 0 0 rgba(229,9,20,0)',
                    '0 0 16px 2px rgba(229,9,20,0.15)',
                    '0 0 0 0 rgba(229,9,20,0)',
                  ],
                }}
                transition={{ duration: 2.5, repeat: Infinity }}
              >
                <Sparkles className="w-3.5 h-3.5 text-red-400" />
                <span>Premium Streaming</span>
              </motion.div>

              <h2 className="text-[28px] sm:text-3xl font-bold text-white mb-1.5 tracking-tight">
                Welcome Back
              </h2>
              <p className="text-gray-400 text-sm">
                Sign in to start watching
              </p>
            </div>

            {/* Error */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -8, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: -8, height: 0 }}
                  className="mb-5 p-3 bg-red-950/40 border border-red-800/40 rounded-xl"
                >
                  <p className="text-sm text-red-300 text-center font-medium">{error}</p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* ── Buttons ── */}
            <div className="space-y-3 mb-7">
              {/* Google — primary CTA, tallest */}
              <motion.button
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                onHoverStart={() => setHoveredButton('google')}
                onHoverEnd={() => setHoveredButton(null)}
                whileHover={{ scale: 1.015, y: -2 }}
                whileTap={{ scale: 0.975 }}
                className="signin-btn signin-btn-google"
              >
                <motion.div
                  className="signin-btn-shine"
                  animate={hoveredButton === 'google' ? { x: ['-100%', '200%'] } : {}}
                  transition={{ duration: 0.5 }}
                />
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    <span>Continue with Google</span>
                  </>
                )}
              </motion.button>

              {/* Activation */}
              <motion.button
                onClick={handleActivationCode}
                onHoverStart={() => setHoveredButton('activation')}
                onHoverEnd={() => setHoveredButton(null)}
                whileHover={{ scale: 1.015, y: -2 }}
                whileTap={{ scale: 0.975 }}
                className="signin-btn signin-btn-activation"
              >
                <motion.div
                  className="signin-btn-shine"
                  animate={hoveredButton === 'activation' ? { x: ['-100%', '200%'] } : {}}
                  transition={{ duration: 0.5 }}
                />
                <Tv className="w-5 h-5 flex-shrink-0" />
                <span>Sign in with activation code</span>
              </motion.button>

              {/* Divider */}
              <div className="flex items-center gap-4 py-0.5">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                <span className="text-[11px] text-gray-500 uppercase tracking-[0.2em] font-semibold select-none">or</span>
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              </div>

              {/* Guest */}
              <motion.button
                onClick={handleGuestMode}
                onHoverStart={() => setHoveredButton('guest')}
                onHoverEnd={() => setHoveredButton(null)}
                whileHover={{ scale: 1.015, y: -1 }}
                whileTap={{ scale: 0.975 }}
                className="signin-btn signin-btn-guest"
              >
                <Play className="w-4 h-4 flex-shrink-0" />
                <span>Continue as Guest</span>
              </motion.button>
            </div>

            {/* Legal */}
            <p className="text-center text-[11px] text-gray-500 leading-relaxed">
              By continuing, you agree to our{' '}
              <span className="text-gray-400 hover:text-red-400 cursor-pointer transition-colors">Terms</span>
              {' & '}
              <span className="text-gray-400 hover:text-red-400 cursor-pointer transition-colors">Privacy Policy</span>
            </p>
          </div>
        </motion.div>

        {/* ── Compact trust strip ── */}
        <motion.div
          variants={itemVariants}
          className="signin-trust-strip"
        >
          <span>HD Quality</span>
          <span className="signin-trust-dot" />
          <span>Instant Play</span>
          <span className="signin-trust-dot" />
          <span>100% Private</span>
        </motion.div>
      </motion.div>
    </div>
  );
}
